import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TrainingViewer from './pages/TrainingViewer';
import TestPage from './pages/TestPage';
import LeaderboardPage from './pages/LeaderboardPage';
import CertificatePage from './pages/CertificatePage';
import ModuleEditorPage from './pages/ModuleEditorPage';
import PathwayReviewPage from './pages/PathwayReviewPage';
import MyDocumentsPage from './pages/MyDocumentsPage';
import CreateTestPage from './pages/CreateTestPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import AppLayout from './components/AppLayout';
import './App.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function DashboardRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin' || user.role === 'manager') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/" element={<DashboardRedirect />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><StaffDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AppLayout><AdminDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/training/:moduleId" element={<ProtectedRoute><AppLayout><TrainingViewer /></AppLayout></ProtectedRoute>} />
      <Route path="/test/:testId" element={<ProtectedRoute><AppLayout><TestPage /></AppLayout></ProtectedRoute>} />
      <Route path="/refresher/:refresherId" element={<ProtectedRoute><AppLayout><TestPage /></AppLayout></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><AppLayout><LeaderboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/certificate/:moduleId" element={<ProtectedRoute><AppLayout><CertificatePage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/pathway/:docId" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AppLayout><PathwayReviewPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/module/:moduleId" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AppLayout><ModuleEditorPage /></AppLayout></ProtectedRoute>} />
      <Route path="/my-documents" element={<ProtectedRoute><AppLayout><MyDocumentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/create-test" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AppLayout><CreateTestPage /></AppLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
